import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { usePasswordValidation } from '../hooks/usePasswordValidation';
import { useSignUp } from '../hooks/useSignUp';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { toast } from 'sonner';
import { EmailConfirmationDialog } from './EmailConfirmationDialog';
import { SocialAuth } from '@/auth/components/SocialAuth';

export const SignUpForm = ({ toggleMode }: { toggleMode: () => void }) => {
  const { checkPendingCheckout } = useStripeCheckout();
  const {
    passwordStrength,
    passwordFeedback,
    validatePassword,
    getProgressColor
  } = usePasswordValidation();
  
  const {
    loading,
    error,
    signUp,
    signupComplete,
    setError
  } = useSignUp();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [checkoutChecked, setCheckoutChecked] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (signupComplete && !checkoutChecked) {
      console.log('Signup complete, checking for pending checkout');
      setCheckoutChecked(true);
      
      const timer = setTimeout(() => {
        const hasPendingCheckout = checkPendingCheckout();
        
        if (!hasPendingCheckout) {
          setShowConfirmDialog(true);
        } else {
          toast.success('Account created! Redirecting to checkout...');
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [signupComplete, checkPendingCheckout, checkoutChecked]);

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationResult = validatePassword(password);
    if (!validationResult.isValid) {
      setError('Password does not meet security requirements');
      return;
    }
    
    const result = await signUp(email, password);
    
    if (result.success) {
      console.log('Signup succeeded, user:', result.user);
      if (result.confirmEmail) {
        setShowConfirmDialog(true);
      }
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    if (newPassword) {
      validatePassword(newPassword);
    }
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    toggleMode();
  };

  return (
    <>
      <form onSubmit={handleEmailSignUp} className="space-y-4">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
          required
        />

        <div className="space-y-2">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            required
          />
          
          <PasswordStrengthIndicator
            password={password}
            strength={passwordStrength}
            feedback={passwordFeedback}
            progressColor={getProgressColor(passwordStrength)}
          />
        </div>

        {error && (
          <Alert variant="destructive" className="bg-red-900/50 border-red-800">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <Button 
          type="submit" 
          className={`w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-all duration-300 rounded-xl py-3
            ${isButtonPressed ? 'ring-4 ring-blue-500/50 transform scale-[0.98] shadow-inner' : 'shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30'}`}
          disabled={loading}
          onMouseDown={() => setIsButtonPressed(true)}
          onMouseUp={() => setIsButtonPressed(false)}
          onMouseLeave={() => setIsButtonPressed(false)}
        >
          {loading ? 'Creating account...' : 'Sign up with Email'}
        </Button>
      </form>

      <SocialAuth />

      <EmailConfirmationDialog 
        isOpen={showConfirmDialog} 
        onClose={closeConfirmDialog} 
        email={email}
      />
    </>
  );
};
